// Testimonials Slider Component
// Uses GSAP for smooth horizontal looping slider with autoplay and drag support
// Import from shared GSAP module for better code splitting
import { gsap, ScrollTrigger } from '../gsap-config.js';

// GSAP Club plugins (require Club membership)
// These will be loaded asynchronously - if not available, draggable will be disabled
let CustomEase, Draggable, InertiaPlugin;
let clubPluginsLoaded = false;

// Async function to load Club plugins
async function loadClubPlugins() {
  if (clubPluginsLoaded) return;
  
  try {
    // Try to load CustomEase
    try {
      const customEaseModule = await import("gsap/CustomEase");
      CustomEase = customEaseModule.CustomEase || customEaseModule.default;
      if (CustomEase && typeof CustomEase.create === "function") {
        try {
          // Register CustomEase - it's safe to call registerPlugin multiple times
          gsap.registerPlugin(CustomEase);
          CustomEase.create("osmo-ease", "0.625, 0.05, 0, 1");
        } catch (regError) {
          console.warn("Error registering GSAP CustomEase plugin:", regError);
          CustomEase = null;
        }
      }
    } catch (e) {
      // CustomEase not available - continue without it
      CustomEase = null;
    }
    
    // Try to load Draggable
    try {
      const draggableModule = await import("gsap/Draggable");
      Draggable = draggableModule.Draggable || draggableModule.default;
      // Validate that Draggable is actually a valid plugin
      if (Draggable && typeof Draggable.create === "function") {
        try {
          // Register Draggable - it's safe to call registerPlugin multiple times
          gsap.registerPlugin(Draggable);
        } catch (regError) {
          console.warn("Error registering GSAP Draggable plugin:", regError);
          Draggable = null;
        }
      } else {
        Draggable = null;
      }
    } catch (e) {
      // Draggable not available - slider will work without drag functionality
      Draggable = null;
      console.warn("GSAP Draggable plugin not available. Drag functionality disabled.");
    }
    
    // Try to load InertiaPlugin
    try {
      const inertiaModule = await import("gsap/InertiaPlugin");
      InertiaPlugin = inertiaModule.InertiaPlugin || inertiaModule.default;
      if (InertiaPlugin) {
        try {
          // Register InertiaPlugin - it's safe to call registerPlugin multiple times
          gsap.registerPlugin(InertiaPlugin);
        } catch (regError) {
          console.warn("Error registering GSAP InertiaPlugin:", regError);
          InertiaPlugin = null;
        }
      }
    } catch (e) {
      // InertiaPlugin not available - continue without momentum scrolling
      InertiaPlugin = null;
    }
    
    clubPluginsLoaded = true;
  } catch (e) {
    // General error - mark as loaded to prevent retries
    clubPluginsLoaded = true;
    console.warn("Error loading GSAP Club plugins:", e);
  }
}

// Register ScrollTrigger (already registered in gsap-config, but safe to call again)
gsap.registerPlugin(ScrollTrigger);

function initSliders() {
  const sliderWrappers = gsap.utils.toArray(document.querySelectorAll('[data-centered-slider="wrapper"]'));

  sliderWrappers.forEach((sliderWrapper) => {
    const slides = gsap.utils.toArray(sliderWrapper.querySelectorAll('[data-centered-slider="slide"]'));
    const bullets = gsap.utils.toArray(sliderWrapper.querySelectorAll('[data-centered-slider="bullet"]'));
    const prevButton = sliderWrapper.querySelector('[data-centered-slider="prev-button"]');
    const nextButton = sliderWrapper.querySelector('[data-centered-slider="next-button"]');

    let activeElement;
    let activeBullet;
    let currentIndex = 0;
    let autoplay;

    // Autoplay is now enabled/disabled via a boolean attribute.
    const autoplayEnabled = sliderWrapper.getAttribute('data-slider-autoplay') === 'true';
    
    // If enabled, get the autoplay duration (in seconds) from the separate attribute.
    const autoplayDuration = autoplayEnabled ? parseFloat(sliderWrapper.getAttribute('data-slider-autoplay-duration')) || 0 : 0;

    // Dynamically assign unique IDs to slides
    slides.forEach((slide, i) => {
      slide.setAttribute("id", `slide-${i}`);
    });
    
    // Set ARIA attributes on bullets if they exist
    if (bullets && bullets.length > 0) {
      bullets.forEach((bullet, i) => {
        bullet.setAttribute("aria-controls", `slide-${i}`);
        bullet.setAttribute("aria-selected", i === currentIndex ? "true" : "false");
      });
    }

    // Enable draggable if Draggable plugin is available
    // InertiaPlugin is optional - if available, enables momentum-based dragging
    const enableDraggable = Draggable && typeof Draggable.create === "function";

    const loop = horizontalLoop(slides, {
      paused: true,
      draggable: enableDraggable,
      center: true,
      onChange: (element, index) => {
        currentIndex = index;
        
        if (activeElement) activeElement.classList.remove("active");
        element.classList.add("active");
        activeElement = element;

        if (bullets && bullets.length > 0) {
          if (activeBullet) activeBullet.classList.remove("active");
          if (bullets[index]) {
            bullets[index].classList.add("active");
            activeBullet = bullets[index];
          }
          bullets.forEach((bullet, i) => {
            bullet.setAttribute("aria-selected", i === index ? "true" : "false");
          });
        }
        
      }
    });
    
    // On initialization, center the slider (start at index 2 if available, otherwise 0)
    const initialIndex = slides.length > 2 ? 2 : 0;
    loop.toIndex(initialIndex, { duration: 0.01 });

    function startAutoplay() {
      if (autoplayDuration > 0 && !autoplay) {
        const repeat = () => {
          loop.next({ ease: CustomEase ? "osmo-ease" : "power2.out", duration: 0.725 });
          autoplay = gsap.delayedCall(autoplayDuration, repeat);
        };
        autoplay = gsap.delayedCall(autoplayDuration, repeat);
      }
    }

    function stopAutoplay() {
      if (autoplay) {
        autoplay.kill();
        autoplay = null;
      }
    }

    // Start/stop autoplay based on viewport visibility via ScrollTrigger
    ScrollTrigger.create({
      trigger: sliderWrapper,
      start: "top bottom",
      end: "bottom top",
      onEnter: startAutoplay,
      onLeave: stopAutoplay,
      onEnterBack: startAutoplay,
      onLeaveBack: stopAutoplay
    });

    // Pause autoplay on mouse hover over the slider
    sliderWrapper.addEventListener("mouseenter", stopAutoplay);
    sliderWrapper.addEventListener("mouseleave", () => {
      if (ScrollTrigger.isInViewport(sliderWrapper)) startAutoplay();
    });

    // Slide click event for direct navigation
    slides.forEach((slide, i) => {
      slide.addEventListener("click", () => {
        loop.toIndex(i, { ease: CustomEase ? "osmo-ease" : "power2.out", duration: 0.725 });
      });
    });

    // Bullets click event for direct navigation (if available)
    if (bullets && bullets.length > 0) {
      bullets.forEach((bullet, i) => {
        bullet.addEventListener("click", () => {
          loop.toIndex(i, { ease: CustomEase ? "osmo-ease" : "power2.out", duration: 0.725 });
          if (activeBullet) activeBullet.classList.remove("active");
          bullet.classList.add("active");
          activeBullet = bullet;
          bullets.forEach((b, j) => {
            b.setAttribute("aria-selected", j === i ? "true" : "false");
          });
        });
      });
    }

    // Prev/Next button listeners (if the buttons exist)
    if (prevButton) {
      prevButton.addEventListener("click", () => {
        let newIndex = currentIndex - 1;
        if (newIndex < 0) newIndex = slides.length - 1;
        loop.toIndex(newIndex, { ease: CustomEase ? "osmo-ease" : "power2.out", duration: 0.725 });
      });
    }

    if (nextButton) {
      nextButton.addEventListener("click", () => {
        let newIndex = currentIndex + 1;
        if (newIndex >= slides.length) newIndex = 0;
        loop.toIndex(newIndex, { ease: CustomEase ? "osmo-ease" : "power2.out", duration: 0.725 });
      });
    }
    
  });
}

// Initialize sliders when DOM is ready
// Load Club plugins first, then initialize sliders
async function initialize() {
  await loadClubPlugins();
  initSliders();
}

if (document.readyState === 'loading') {
  document.addEventListener("DOMContentLoaded", initialize);
} else {
  initialize();
}

// GSAP Helper function to create a looping slider
// Read more: https://gsap.com/docs/v3/HelperFunctions/helpers/seamlessLoop
function horizontalLoop(items, config) {
  let timeline;
  items = gsap.utils.toArray(items);
  config = config || {};
  gsap.context(() => { 
    let onChange = config.onChange,
      lastIndex = 0,
      tl = gsap.timeline({repeat: config.repeat, onUpdate: onChange && function() {
          let i = tl.closestIndex();
          if (lastIndex !== i) {
            lastIndex = i;
            onChange(items[i], i);
          }
        }, paused: config.paused, defaults: {ease: "none"}, onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)}),
      length = items.length,
      startX = items[0].offsetLeft,
      times = [],
      widths = [],
      spaceBefore = [],
      xPercents = [],
      curIndex = 0,
      indexIsDirty = false,
      center = config.center,
      pixelsPerSecond = (config.speed || 1) * 100,
      snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1),
      timeOffset = 0,
      container = center === true ? items[0].parentNode : gsap.utils.toArray(center)[0] || items[0].parentNode,
      totalWidth,
      getTotalWidth = () => items[length-1].offsetLeft + xPercents[length-1] / 100 * widths[length-1] - startX + spaceBefore[0] + items[length-1].offsetWidth * gsap.getProperty(items[length-1], "scaleX") + (parseFloat(config.paddingRight) || 0),
      populateWidths = () => {
        let b1 = container.getBoundingClientRect(), b2;
        items.forEach((el, i) => {
          widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
          xPercents[i] = snap(parseFloat(gsap.getProperty(el, "x", "px")) / widths[i] * 100 + gsap.getProperty(el, "xPercent"));
          b2 = el.getBoundingClientRect();
          spaceBefore[i] = b2.left - (i ? b1.right : b1.left);
          b1 = b2;
        });
        gsap.set(items, {
          xPercent: i => xPercents[i]
        });
        totalWidth = getTotalWidth();
      },
      timeWrap,
      populateOffsets = () => {
        timeOffset = center ? tl.duration() * (container.offsetWidth / 2) / totalWidth : 0;
        center && times.forEach((t, i) => {
          times[i] = timeWrap(tl.labels["label" + i] + tl.duration() * widths[i] / 2 / totalWidth - timeOffset);
        });
      },
      getClosest = (values, value, wrap) => {
        let i = values.length,
          closest = 1e10,
          index = 0, d;
        while (i--) {
          d = Math.abs(values[i] - value);
          if (d > wrap / 2) {
            d = wrap - d;
          }
          if (d < closest) {
            closest = d;
            index = i;
          }
        }
        return index;
      },
      populateTimeline = () => {
        let i, item, curX, distanceToStart, distanceToLoop;
        tl.clear();
        for (i = 0; i < length; i++) {
          item = items[i];
          curX = xPercents[i] / 100 * widths[i];
          distanceToStart = item.offsetLeft + curX - startX + spaceBefore[0];
          distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
          tl.to(item, {xPercent: snap((curX - distanceToLoop) / widths[i] * 100), duration: distanceToLoop / pixelsPerSecond}, 0)
            .fromTo(item, {xPercent: snap((curX - distanceToLoop + totalWidth) / widths[i] * 100)}, {xPercent: xPercents[i], duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond, immediateRender: false}, distanceToLoop / pixelsPerSecond)
            .add("label" + i, distanceToStart / pixelsPerSecond);
          times[i] = distanceToStart / pixelsPerSecond;
        }
        timeWrap = gsap.utils.wrap(0, tl.duration());
      },
      refresh = (deep) => {
        let progress = tl.progress();
        tl.progress(0, true);
        populateWidths();
        deep && populateTimeline();
        populateOffsets();
        deep && tl.draggable ? tl.time(times[curIndex], true) : tl.progress(progress, true);
      },
      onResize = () => refresh(true),
      proxy;
    gsap.set(items, {x: 0});
    populateWidths();
    populateTimeline();
    populateOffsets();
    window.addEventListener("resize", onResize);
    function toIndex(index, vars) {
      vars = vars || {};
      (Math.abs(index - curIndex) > length / 2) && (index += index > curIndex ? -length : length); // always go in the shortest direction
      let newIndex = gsap.utils.wrap(0, length, index),
        time = times[newIndex];
      if (time > tl.time() !== index > curIndex && index !== curIndex) { // if we're wrapping the timeline's playhead, make the proper adjustments
        time += tl.duration() * (index > curIndex ? 1 : -1);
      }
      if (time < 0 || time > tl.duration()) {
        vars.modifiers = {time: timeWrap};
      }
      curIndex = newIndex;
      vars.overwrite = true;
      gsap.killTweensOf(proxy);    
      return vars.duration === 0 ? tl.time(timeWrap(time)) : tl.tweenTo(time, vars);
    }
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.closestIndex = setCurrent => {
      let index = getClosest(times, tl.time(), tl.duration());
      if (setCurrent) {
        curIndex = index;
        indexIsDirty = false;
      }
      return index;
    };
    tl.current = () => indexIsDirty ? tl.closestIndex(true) : curIndex;
    tl.next = vars => toIndex(tl.current()+1, vars);
    tl.previous = vars => toIndex(tl.current()-1, vars);
    tl.times = times;
    tl.progress(1, true).progress(0, true); // pre-render for performance
    if (config.reversed) {
      tl.vars.onReverseComplete();
      tl.reverse();
    }
    if (config.draggable && Draggable && typeof Draggable.create === "function") {
      proxy = document.createElement("div")
      let wrap = gsap.utils.wrap(0, 1),
        ratio, startProgress, draggable, dragSnap, lastSnap, initChangeX, wasPlaying,
        align = () => tl.progress(wrap(startProgress + (draggable.startX - draggable.x) * ratio)),
        syncIndex = () => tl.closestIndex(true);
      
      // Build Draggable config - inertia is optional
      const draggableConfig = {
        trigger: items[0].parentNode,
        type: "x",
        onPressInit() {
          let x = this.x;
          gsap.killTweensOf(tl);
          wasPlaying = !tl.paused();
          tl.pause();
          startProgress = tl.progress();
          refresh();
          ratio = 1 / totalWidth;
          initChangeX = (startProgress / -ratio) - x;
          gsap.set(proxy, {x: startProgress / -ratio});
        },
        onDrag: align,
        overshootTolerance: 0,
        snap(value) {
          if (Math.abs(startProgress / -ratio - this.x) < 10) {
            return lastSnap + initChangeX
          }
          let time = -(value * ratio) * tl.duration(),
            wrappedTime = timeWrap(time),
            snapTime = times[getClosest(times, wrappedTime, tl.duration())],
            dif = snapTime - wrappedTime;
          Math.abs(dif) > tl.duration() / 2 && (dif += dif < 0 ? tl.duration() : -tl.duration());
          lastSnap = (time + dif) / tl.duration() / -ratio;
          return lastSnap;
        },
        onRelease() {
          syncIndex();
        }
      };
      
      // Add inertia and throw callbacks only if InertiaPlugin is available
      if (InertiaPlugin) {
        draggableConfig.inertia = true;
        draggableConfig.onThrowUpdate = align;
        draggableConfig.onThrowComplete = () => {
          syncIndex();
          wasPlaying && tl.play();
        };
        // Update onRelease to check isThrowing
        const originalOnRelease = draggableConfig.onRelease;
        draggableConfig.onRelease = function() {
          originalOnRelease();
          if (this.isThrowing) {
            indexIsDirty = true;
          }
        };
      } else {
        // Without inertia, snap to nearest slide on release
        const originalOnRelease = draggableConfig.onRelease;
        draggableConfig.onRelease = function() {
          originalOnRelease();
          // Snap to nearest slide
          syncIndex();
          const currentTime = tl.time();
          const closestTime = times[getClosest(times, currentTime, tl.duration())];
          tl.tweenTo(closestTime, { duration: 0.3, ease: "power2.out" });
          wasPlaying && tl.play();
        };
      }
      
      try {
        draggable = Draggable.create(proxy, draggableConfig)[0];
        tl.draggable = draggable;
      } catch (e) {
        console.warn("Failed to initialize Draggable:", e);
        // Continue without draggable functionality
      }
    }
    tl.closestIndex(true);
    lastIndex = curIndex;
    onChange && onChange(items[curIndex], curIndex);
    timeline = tl;
    return () => window.removeEventListener("resize", onResize); 
  });
  return timeline;
  
}
